/**
 * PillarTrajectoryHint — AFI-G3
 *
 * Small inline hint displayed beneath each pillar score card showing the
 * expected movement window for that pillar. Reads from the single source-of-
 * truth projection-windows config; AFI-F1 calibration updates that config and
 * this component picks up the change automatically.
 *
 * Accessibility: WCAG AA contrast, keyboard-reachable tooltip, screen-reader
 * aria-label with detailed description.
 */

import { PROJECTION_WINDOWS } from '@fixmyseo/shared/projection-windows';
import type { Pillar } from '@fixmyseo/shared/projection-windows';

interface Props {
  pillar: Pillar;
}

export function PillarTrajectoryHint({ pillar }: Props) {
  const pw = PROJECTION_WINDOWS[pillar];
  const tooltipId = `trajectory-tooltip-${pillar}`;

  return (
    <span className="relative inline-flex items-center gap-1 group/trajectory">
      {/* Clock icon */}
      <span aria-hidden="true" className="text-slate-400 text-xs leading-none">⏱</span>

      {/* Trigger text — keyboard focusable */}
      <button
        type="button"
        aria-describedby={tooltipId}
        aria-label={`Expected movement for ${pw.pillarLabel}: ${pw.displayLabel}. ${pw.detailedDescription}`}
        className="text-xs text-slate-500 hover:text-slate-700 focus-visible:text-slate-700
                   bg-transparent border-0 p-0 cursor-help leading-none
                   focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                   focus-visible:outline-slate-400 rounded-sm"
      >
        Expected movement:{' '}
        <strong className="font-semibold text-slate-600">{pw.displayLabel}</strong>
      </button>

      {/* Tooltip — CSS-driven, same pattern as HeadlineBand InfoButton */}
      <div
        id={tooltipId}
        role="tooltip"
        className="hidden group-hover/trajectory:block group-focus-within/trajectory:block
                   absolute bottom-full left-0 mb-2 w-72 bg-slate-800 text-white text-xs
                   rounded-lg p-3 shadow-xl z-50 pointer-events-auto"
      >
        <p className="leading-relaxed">{pw.detailedDescription}</p>
        {/* Caret */}
        <div
          aria-hidden="true"
          className="absolute top-full left-4 w-0 h-0 border-x-[5px] border-x-transparent border-t-[5px] border-t-slate-800"
        />
      </div>
    </span>
  );
}
